// ─── 请求参数 ───

export const ScenarioEnableStatus = {
  SE_UNKNOWN: 'SE_UNKNOWN',
  SCENARIO_ENABLE: 'SCENARIO_ENABLE',
  SCENARIO_DISABLE: 'SCENARIO_DISABLE',
  SCENARIO_POLICY_ENABLE: 'SCENARIO_POLICY_ENABLE',
  SCENARIO_POLICY_DISABLE: 'SCENARIO_POLICY_DISABLE',
} as const;

export interface OfficialScenarioListParams {
  region?: string;
  storeGroupId?: string;
  scenarioEnableStatus?: string;
  keyword?: string;
  language?: string;
}

export interface OfficialScenarioDetailParams extends OfficialScenarioListParams {
  intentId: string | number;
  storeId?: string;
}

export interface UpdateOfficialScenarioParams {
  region: string;
  storeGroupId: string | number;
  intentId: string | number;
  enable: boolean;
}

export interface UpdateOfficialScenarioPolicyParams {
  region: string;
  storeGroupId: string | number;
  policyId: string | number;
  enable: boolean;
}

export interface UpdateOfficialScenarioSubPolicyParams {
  region: string;
  storeGroupId: string | number;
  subPolicyId: string | number;
  receptionMode: ApiReceptionMode;
  todoType: string;
}

export interface OfficialScenarioTodoPolicyTreeNode {
  type_name?: string;
  level?: number;
  sub_types?: OfficialScenarioTodoPolicyTreeNode[];
}

export interface OfficialScenarioTodoPolicy {
  type: number;
  name: string;
  source: number;
  sub_type_trees?: OfficialScenarioTodoPolicyTreeNode[];
}

export interface OfficialScenarioLogParams {
  region: string;
  storeGroupId: string | number;
  intentId: string | number;
  offsetId: number;
  pageSize: number;
  language?: string;
}

interface OfficialScenarioLogEnableContent {
  enable?: string | boolean;
}

interface OfficialScenarioLogModeContent {
  receptionMode?: string;
  todoType?: string;
}

export interface OfficialScenarioLogRecord {
  id: string;
  userId?: string;
  userName?: string;
  intentId?: string;
  intentName?: string;
  policyId?: string;
  policyName?: string;
  subPolicyId?: string;
  subPolicyName?: string;
  operateType?: string;
  createTime?: string | number;
  updateScenarioContent?: {
    before?: OfficialScenarioLogEnableContent;
    after?: OfficialScenarioLogEnableContent;
  };
  updatePolicyContent?: {
    before?: OfficialScenarioLogEnableContent;
    after?: OfficialScenarioLogEnableContent;
  };
  updateSubPolicyContent?: {
    before?: OfficialScenarioLogModeContent;
    after?: OfficialScenarioLogModeContent;
  };
}

export interface OfficialScenarioLogResponse {
  code: number;
  msg: string;
  nextOffsetId?: number;
  hasNext?: boolean | string;
  data: OfficialScenarioLogRecord[];
}

export const OfficialScenarioLogOperationType = {
  UPDATE_SCENARIO_SWITCH: 'UPDATE_SCENARIO_SWITCH',
  UPDATE_POLICY_SWITCH: 'UPDATE_POLICY_SWITCH',
  UPDATE_RECEPTION_METHOD: 'UPDATE_RECEPTION_METHOD',
} as const;

// ─── 条件规则 ───

export interface Condition {
  parameter: string;
  operator: 'EQ' | 'CONTAIN' | 'MATCH_ANY' | 'EQ' | 'LT' | 'GT' | 'BETWEEN' | 'NO_LIMIT';
  expectList: string[];
}

export interface ConditionGroup {
  conditions: Condition[];
}

export interface ConditionRule {
  logicOperator: 'OR' | 'AND';
  conditionGroups?: ConditionGroup[];
}

export interface PolicyGroupStoreInfo {
  storeId?: string | number;
  storeName?: string;
  alias?: string;
  externalType?: string;
  region?: string;
  [key: string]: unknown;
}

// ─── 策略节点（Policy / SubPolicy 共享结构） ───

export type ApiReceptionMode =
  | 'RM_UNKNOWN'
  | 'AI_REPLY'
  | 'AI_REPLY_AND_HUMAN'
  | 'AI_REPLY_AND_LOGISTICS'
  | 'AI_REPLY_AND_LOGISTICS_HUMAN'
  | 'AI_REPLY_AND_TODO'
  | 'NOT_REPLY'
  | 'NOT_REPLY_AND_TODO'
  | 'NOT_REPLY_WAITING'
  | 'AI_REPLY_AND_HUMAN_TODO';

export type ReplyMode =
  | 'RM_UNKNOWN'
  | 'AI'
  | 'ITEM_AI'
  | 'FIXED_REPLY'
  | 'SIZE_SUGGESTION_AI'
  | 'NOT_REPLY';

export interface ReplyContentTextItem {
  text?: string;
}

export interface ReplyContentTextGroup {
  textContent?: ReplyContentTextItem[];
}

export interface ReplyContentImageItem {
  resourceId?: number;
  imageUrl?: string;
}

export interface ReplyContentItem {
  type?: 'TEXT' | 'IMAGE' | 'LOGISTICS_CARD';
  text?: string;
  textContents?: ReplyContentTextItem[] | ReplyContentTextGroup;
  imageContents?: ReplyContentImageItem[];
}

interface BasePolicyNode {
  name: string;
  enable?: boolean;
  priority?: number;
  intentId?: string;
  conditionRule?: ConditionRule;
  replyMode?: ReplyMode;
  replyLogicDesc?: string;
  replyExample?: string;
  replyContent?: ReplyContentItem[];
  originalReceptionMode?: ApiReceptionMode;
  receptionMode?: ApiReceptionMode;
  todoType?: string;
  subPolicy?: SubPolicy[];
}

// ─── 子策略 ───

export interface SubPolicy extends BasePolicyNode {
  subPolicyId: string;
}

// ─── 策略（分支） ───

export interface Policy extends BasePolicyNode {
  policyId: string;
  intentId: string;
  enable: boolean;
  conditionRule: ConditionRule;
}

export interface PolicyGroup {
  policy: Policy[];
  allStore: boolean;
  storeInfo?: PolicyGroupStoreInfo[];
}

// ─── 场景 ───

export interface Scenario {
  intentId: string;
  name: string;
  index?: number;
  desc?: string;
  example?: string[];
  groupId: string;
  groupName: string;
  heatValue?: number | string;
  updateTime?: number | string;
  enable: boolean;
  policyCount: string;
  enablePolicyCount: string;
  policyGroup?: PolicyGroup[];
}

// ─── 意图分组（分类标签） ───

export interface IntentGroup {
  intentGroupId: string;
  name: string;
  scenario: Scenario[];
}
